import { useQuery } from 'react-query'
import { useEffect, useState } from 'react'
import { resolved, useQuery as useGqtyQuery } from '@hapana/shared/web/services/gqty'
import {
  AVAILABLE_PLAN_OPTIONS_QUERY_KEY,
  PREVIEW_PLAN_CHANGE_QUERY_KEY,
  SERVICE_TEMPLATE_AGREEMENT_QUERY_KEY,
} from '../../graphql/client-packages.queries'
import {
  fetchAvailablePlanOptions,
  fetchPreviewMembershipPlanChange,
  fetchServiceTemplateAgreement,
} from '../../graphql/client-packages.service'

export interface PlanOption {
  serviceTemplateId: string
  name: string
  price: string
  direction: 'UPGRADE' | 'CURRENT' | 'DOWNGRADE'
  priceDelta: string
  tierOrder?: number | null
}

interface AvailablePlanOptionsResponse {
  availablePlanOptions: PlanOption[]
}

interface UseAvailablePlanOptionsResult {
  planOptions: PlanOption[]
  isLoading: boolean
  error: Error | null
}

export function useAvailablePlanOptions(
  siteId: string,
  serviceId: string,
  siteSessionTypeId?: string
): UseAvailablePlanOptionsResult {
  const { data, isLoading, error } = useQuery<AvailablePlanOptionsResponse>(
    [AVAILABLE_PLAN_OPTIONS_QUERY_KEY, { siteId, serviceId, siteSessionTypeId }],
    fetchAvailablePlanOptions.query as any,
    { enabled: Boolean(siteId) && Boolean(serviceId), refetchOnMount: 'always' }
  )

  const planOptions: PlanOption[] = [...(data?.availablePlanOptions ?? [])].sort((a, b) => {
    if (a.tierOrder != null && b.tierOrder != null) return a.tierOrder - b.tierOrder
    if (a.tierOrder != null) return -1
    if (b.tierOrder != null) return 1
    return a.name.localeCompare(b.name)
  })

  return {
    planOptions,
    isLoading,
    error: error as Error | null,
  }
}

interface UseCalculateCartParams {
  siteId: string
  serviceId: string
  selectedPlan: PlanOption | null
  effectiveType: 'Immediate' | 'EndOfCycle'
}

interface UseCalculateCartResult {
  proratedTotalAmount: string | null
  isLoadingProration: boolean
}

export function useCalculateCart({
  siteId,
  serviceId,
  selectedPlan,
  effectiveType,
}: UseCalculateCartParams): UseCalculateCartResult {
  const isUpgrade = selectedPlan?.direction === 'UPGRADE' && parseFloat(selectedPlan.priceDelta) > 0
  const enabled =
    isUpgrade &&
    effectiveType === 'Immediate' &&
    Boolean(siteId) &&
    Boolean(serviceId) &&
    Boolean(selectedPlan)

  const variables = enabled
    ? {
        siteId,
        serviceId,
        toServiceTemplateId: selectedPlan!.serviceTemplateId,
      }
    : null

  const { data, isLoading } = useQuery<{
    previewMembershipPlanChange: { proratedAmount: string | null } | null
  }>([PREVIEW_PLAN_CHANGE_QUERY_KEY, variables], fetchPreviewMembershipPlanChange.query as any, {
    enabled,
  })

  const proratedTotalAmount: string | null =
    data?.previewMembershipPlanChange?.proratedAmount ?? null

  return {
    proratedTotalAmount,
    isLoadingProration: isLoading && enabled,
  }
}

export interface AgreementRequirement {
  agreementEnabled: boolean
  agreementText: string | null
  siteAgreementTemplateId: string | null
  name: string | null
  type: string | null
  description: string | null
  price: string | null
  oneTimeSetupFee: string | null
  billingCycleAmount: number | null
  billingCycleUnit: string | null
  autoRenewEnabled: boolean
  toServiceTemplateId: string | null
  autoRenewPackageName: string | null
  lengthAmount: number | null
  lengthUnit: string | null
  isAllSharedSites: boolean
  sharedSiteIds: string[]
  sharedLocationNames: string[] | null
  cancellationCharge: string | null
  minimumTermAmount: number | null
  minimumTermUnit: string | null
  secondaryFeeEnabled: boolean
  secondaryFeeAmount: string | null
  secondaryFeeBillingCycleAmount: number | null
  secondaryFeeBillingCycleUnit: string | null
  secondaryFeeFirstPaymentDateEnabled: boolean | null
  secondaryFeeFirstPaymentDateOnDate: string | null
  secondaryFeeFirstPaymentDateAfterAmount: number | null
  secondaryFeeFirstPaymentDateAfterUnit: string | null
  credits: Array<{
    creditsType: string
    amount: number | null
    siteSessionTypeIds: string[]
    order: number | null
  }>
  sessionTypeNamesMap: Record<string, string>
  taxEnabled: boolean
  taxSettingIds: string[]
  taxRates: Array<{ rate: string; setting: 'exclusive' | 'inclusive' }>
}

interface ServiceTemplateAgreementResponse {
  serviceTemplate: {
    serviceTemplateId: string
    name: string | null
    type: string | null
    agreementEnabled: boolean
    agreementText: string | null
    siteAgreementTemplateId: string | null
    description: string | null
    price: string | null
    oneTimeSetupFee: string | null
    billingCycleAmount: number | null
    billingCycleUnit: string | null
    autoRenewEnabled: boolean
    toServiceTemplateId: string | null
    lengthAmount: number | null
    lengthUnit: string | null
    taxEnabled: boolean
    taxSettingIds: string[] | null
    isAllSharedSites: boolean
    sharedSiteIds: string[]
    cancellation: {
      minimumTerm: {
        charge: { amount: string | null } | null
        after: { amount: number | null; unit: string | null } | null
      } | null
    } | null
    secondaryRecurringFee: {
      enabled: boolean
      amount: string | null
      billingCycleAmount: number | null
      billingCycleUnit: string | null
      serviceFirstBillingDate: {
        enabled: boolean
        onDate: string | null
        after: { amount: number | null; unit: string | null } | null
      } | null
    } | null
    credits: Array<{
      creditsType: string
      amount: number | null
      siteSessionTypeIds: string[]
      order: number | null
    }>
  } | null
}

export function useChangePlanAgreement(
  siteId: string,
  serviceTemplateId: string | null | undefined
): { agreementReq: AgreementRequirement | null; isLoading: boolean; error: Error | null } {
  const gqtyQuery = useGqtyQuery({ suspense: false })

  const { data, isLoading, error } = useQuery<ServiceTemplateAgreementResponse>(
    [SERVICE_TEMPLATE_AGREEMENT_QUERY_KEY, { siteId, serviceTemplateId }],
    fetchServiceTemplateAgreement.query as any,
    { enabled: Boolean(serviceTemplateId), refetchOnMount: 'always' }
  )

  const t = data?.serviceTemplate ?? null

  const [autoRenewPackageName, setAutoRenewPackageName] = useState<string | null>(null)
  const [sharedLocationNames, setSharedLocationNames] = useState<string[] | null>(null)
  const [sessionTypeNamesMap, setSessionTypeNamesMap] = useState<Record<string, string>>({})
  const [taxRates, setTaxRates] = useState<
    Array<{ rate: string; setting: 'exclusive' | 'inclusive' }>
  >([])

  useEffect(() => {
    if (!t?.toServiceTemplateId) {
      setAutoRenewPackageName(null)
      return
    }
    const id = t.toServiceTemplateId
    resolved(() => gqtyQuery.serviceTemplate({ serviceTemplateId: id })?.name ?? null, {
      refetch: true,
    })
      .then((name) => setAutoRenewPackageName(name ?? null))
      .catch(() => setAutoRenewPackageName(null))
  }, [t?.toServiceTemplateId])

  useEffect(() => {
    if (!t || t.isAllSharedSites || !t.sharedSiteIds?.length) {
      setSharedLocationNames(t?.isAllSharedSites ? null : [])
      return
    }
    Promise.allSettled(
      t.sharedSiteIds.map((id) =>
        resolved(() => gqtyQuery.site({ siteId: id })?.name ?? null, { refetch: true })
      )
    ).then((results) => {
      const names = results
        .filter((r): r is PromiseFulfilledResult<string | null> => r.status === 'fulfilled')
        .map((r) => r.value)
        .filter((n): n is string => Boolean(n))
      setSharedLocationNames(names)
    })
  }, [t?.sharedSiteIds?.join(','), t?.isAllSharedSites])

  useEffect(() => {
    const allIds = [
      ...new Set((t?.credits ?? []).flatMap((c) => c.siteSessionTypeIds ?? []).filter(Boolean)),
    ]
    if (!allIds.length) {
      setSessionTypeNamesMap({})
      return
    }
    resolved(
      () => {
        const types = gqtyQuery.siteSessionTypesByIds({ siteId, siteSessionTypeIds: allIds })
        return types?.map((st) => ({ id: st.siteSessionTypeId, name: st.name }))
      },
      { refetch: true }
    )
      .then((results) => {
        const map: Record<string, string> = {}
        results?.forEach((st) => {
          if (st?.id && st.name) map[st.id] = st.name
        })
        setSessionTypeNamesMap(map)
      })
      .catch(() => setSessionTypeNamesMap({}))
  }, [siteId, t?.credits?.map((c) => c.siteSessionTypeIds?.join(',')).join('|')])

  useEffect(() => {
    if (!t?.taxEnabled || !t?.taxSettingIds?.length) {
      setTaxRates([])
      return
    }
    const ids = new Set(t.taxSettingIds)
    resolved(
      () => {
        const settings = gqtyQuery.taxSettings({ siteId, enabled: true })
        return settings?.map((s) => ({
          id: s.taxSettingId,
          rate: s.rate,
          setting: s.setting as 'exclusive' | 'inclusive',
        }))
      },
      { refetch: true }
    )
      .then((results) => {
        const rates = (results ?? [])
          .filter((s): s is { id: string; rate: string; setting: 'exclusive' | 'inclusive' } =>
            Boolean(s?.id && ids.has(s.id))
          )
          .map((s) => ({ rate: s.rate, setting: s.setting }))
        setTaxRates(rates)
      })
      .catch(() => setTaxRates([]))
  }, [siteId, t?.taxEnabled, t?.taxSettingIds?.join(',')])

  const agreementReq: AgreementRequirement | null = t
    ? {
        agreementEnabled: t.agreementEnabled,
        agreementText: t.agreementText,
        siteAgreementTemplateId: t.siteAgreementTemplateId,
        name: t.name,
        type: t.type,
        description: t.description,
        price: t.price,
        oneTimeSetupFee: t.oneTimeSetupFee,
        billingCycleAmount: t.billingCycleAmount,
        billingCycleUnit: t.billingCycleUnit,
        autoRenewEnabled: t.autoRenewEnabled,
        toServiceTemplateId: t.toServiceTemplateId ?? null,
        autoRenewPackageName,
        lengthAmount: t.lengthAmount,
        lengthUnit: t.lengthUnit,
        isAllSharedSites: t.isAllSharedSites ?? false,
        sharedSiteIds: t.sharedSiteIds ?? [],
        sharedLocationNames,
        cancellationCharge: t.cancellation?.minimumTerm?.charge?.amount ?? null,
        minimumTermAmount: t.cancellation?.minimumTerm?.after?.amount ?? null,
        minimumTermUnit: t.cancellation?.minimumTerm?.after?.unit ?? null,
        secondaryFeeEnabled: t.secondaryRecurringFee?.enabled ?? false,
        secondaryFeeAmount: t.secondaryRecurringFee?.amount ?? null,
        secondaryFeeBillingCycleAmount: t.secondaryRecurringFee?.billingCycleAmount ?? null,
        secondaryFeeBillingCycleUnit: t.secondaryRecurringFee?.billingCycleUnit ?? null,
        secondaryFeeFirstPaymentDateEnabled:
          t.secondaryRecurringFee?.serviceFirstBillingDate?.enabled ?? null,
        secondaryFeeFirstPaymentDateOnDate:
          t.secondaryRecurringFee?.serviceFirstBillingDate?.onDate ?? null,
        secondaryFeeFirstPaymentDateAfterAmount:
          t.secondaryRecurringFee?.serviceFirstBillingDate?.after?.amount ?? null,
        secondaryFeeFirstPaymentDateAfterUnit:
          t.secondaryRecurringFee?.serviceFirstBillingDate?.after?.unit ?? null,
        credits: t.credits ?? [],
        sessionTypeNamesMap,
        taxEnabled: t.taxEnabled,
        taxSettingIds: t.taxSettingIds ?? [],
        taxRates,
      }
    : null

  return {
    agreementReq,
    isLoading,
    error: error as Error | null,
  }
}
